import { useTranslation } from '@rbx/intl';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import {
  AddIcon,
  Alert,
  AlertTitle,
  Button,
  CheckIcon,
  CloseIcon,
  IconButton,
  makeStyles,
  useSnackbar,
} from '@rbx/ui';
import { getIsAssetOwned, postPurchaseAsset } from '@modules/utilities/utils/assetUtils';
import { useRobloxAuthentication } from '@rbx/auth';

export interface AddToInventoryButtonProps {
  userId?: number;
  assetId: number;
  title: string;
  isAssetOwned: boolean;
  setIsAssetOwned: Dispatch<SetStateAction<boolean>>;
}

const useAddToInventoryButtonStyles = makeStyles()(() => ({
  alertButton: {
    paddingTop: '-8px',
  },
  closeIconButton: {
    marginLeft: '4px',
  },
}));
export default function AddToInventoryButton({
  userId,
  assetId,
  title,
  isAssetOwned,
  setIsAssetOwned,
}: AddToInventoryButtonProps) {
  const { translate } = useTranslation();
  const {
    classes: { alertButton, closeIconButton },
  } = useAddToInventoryButtonStyles();
  const { enqueue, close: closeSnackbar } = useSnackbar();
  const { user: authenticatedUser, login } = useRobloxAuthentication();
  const [currentUserId, setCurrentUserId] = useState<number>();
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const showAddToInventorySnackbar = useCallback(
    (msg: string, isSuccess: boolean, errorTitle?: string) => {
      if (isSuccess) {
        enqueue({
          children: (
            <Alert
              severity='success'
              variant='filled'
              action={[
                <Button
                  color='inherit'
                  size='small'
                  key='openStudio'
                  classes={{ root: alertButton }}>
                  {translate('Button.OpenStudio')}
                </Button>,
                <IconButton
                  color='inherit'
                  classes={{ root: closeIconButton }}
                  aria-label={translate('Label.CloseAlertButton')}
                  size='small'
                  key='alertCloseButton'
                  onClick={closeSnackbar}>
                  <CloseIcon fontSize='small' />
                </IconButton>,
              ]}>
              {msg}
            </Alert>
          ),
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          autoHide: true,
          onClose: closeSnackbar,
        });
      } else {
        enqueue({
          children: (
            <Alert severity='error' variant='filled'>
              <AlertTitle>{errorTitle}</AlertTitle>
            </Alert>
          ),
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          autoHide: true,
          onClose: closeSnackbar,
        });
      }
    },
    [alertButton, closeIconButton, closeSnackbar, enqueue, translate],
  );

  const handleAddToInventoryButtonClick = useCallback(async () => {
    setIsButtonLoading(true);
    if (currentUserId === undefined) {
      await login();
    }

    const isAddedSuccess = await postPurchaseAsset(currentUserId ?? 0, assetId);
    setIsButtonLoading(false);
    if (isAddedSuccess) {
      showAddToInventorySnackbar(
        title
          ? translate('Message.AddToInventorySuccessWithTitle', { title })
          : translate('Message.AddToInventorySuccess'),
        true,
      );
      setIsAssetOwned(true);
    } else {
      showAddToInventorySnackbar(
        translate('Message.AddToInventoryFailedMessage', { title: title ?? '' }),
        false,
        translate('Message.AddToInventoryFailedTitle'),
      );
    }
  }, [
    assetId,
    currentUserId,
    login,
    setIsAssetOwned,
    showAddToInventorySnackbar,
    title,
    translate,
  ]);

  const fetchData = useCallback(async () => {
    if (userId !== undefined) {
      const isCurrentAssetOwned = await getIsAssetOwned(userId, assetId);
      setIsAssetOwned(isCurrentAssetOwned);
    }
  }, [assetId, setIsAssetOwned, userId]);

  useEffect(() => {
    if (userId) {
      setCurrentUserId(userId);
    } else if (authenticatedUser?.id) {
      setCurrentUserId(authenticatedUser.id);
    }
    fetchData();
  }, [authenticatedUser?.id, fetchData, userId]);

  return (
    <Button
      color='primary'
      size='small'
      loading={isButtonLoading}
      disabled={isAssetOwned}
      startIcon={isAssetOwned ? <CheckIcon /> : <AddIcon />}
      onClick={handleAddToInventoryButtonClick}>
      {isAssetOwned ? translate('Button.AddedToInventory') : translate('Button.AddToInventory')}
    </Button>
  );
}
